import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Users as UsersIcon,
  Mail,
  Loader,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Eye,
  Pencil,
  CreditCard,
  Calendar,
} from 'lucide-react';
import {
  getAllPublicUsers,
  updatePublicUser,
  deletePublicUser,
  blockPublicUser,
  unblockPublicUser,
} from '../services/publicUserService';
import { toast } from 'react-toastify';
import { PublicUserProfile } from '../types';
import Modal from '../components/Common/Modal';
import CustomSelect from '../components/Common/CustomSelect';
import { paginationPageWindow } from '../utils/pagination';

const ITEMS_PER_PAGE = 10;

const formatCurrency = (amount: number | string | null | undefined, currency: string = 'USD'): string => {
  const num = Number(amount) || 0;
  const curr = (currency || 'USD').toUpperCase().trim();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${curr} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const subscriptionStatusClasses = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-amber-100 text-amber-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

type FilterState = {
  status: string;
};

const PublicUsers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ status: '' });

  const [userToDelete, setUserToDelete] = useState<PublicUserProfile | null>(null);
  const [userToBlock, setUserToBlock] = useState<PublicUserProfile | null>(null);
  const [userToEdit, setUserToEdit] = useState<PublicUserProfile | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', status: 'active' });
  const [editErrors, setEditErrors] = useState<{ name?: string; email?: string }>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PublicUserProfile | null>(null);
  const [users, setUsers] = useState<PublicUserProfile[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);

  const fetchUsersFromAPI = async () => {
    setIsLoadingUsers(true);

    try {
      const usersList = await getAllPublicUsers();
      setUsers(usersList);
    } catch (error: any) {
      console.error("Failed to fetch public users:", error);

      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response?.data?.message || error.message;

        if (status === 404) {
          toast.error("Public users endpoint not found. Please ensure the backend API is running.");
        } else if (status === 403) {
          toast.error("You don't have permission to access public user management.");
        } else if (status === 401) {
          toast.error("Authentication required. Please login again.");
        } else {
          toast.error(errorMessage || `Failed to load public users (Status: ${status})`);
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your connection.");
      } else {
        toast.error(error.message || "Failed to load public users from server");
      }

      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsersFromAPI();

    // Auto-refetch when window regains focus to reflect user edits immediately
    const handleFocus = () => {
      fetchUsersFromAPI();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const name = user.name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';

      const matchesSearch = name.includes(term) || email.includes(term);

      if (filters.status) {
        const isActive = filters.status === 'active';
        if (user.active !== isActive) return false;
      }

      return matchesSearch;
    });
  }, [users, searchTerm, filters]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const clearFilters = () => {
    setFilters({ status: '' });
    setSearchTerm('');
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await deletePublicUser(userToDelete.userId);
      toast.success('Public user deleted successfully!');
      setUsers(prev => prev.filter(u => u.userId !== userToDelete.userId));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the public user';
      toast.error(errorMessage);
      console.error('Delete error:', error);
    }
  };

  const handleBlockUnblock = async () => {
    if (!userToBlock) return;

    try {
      const isBlocking = userToBlock.active;
      const updatedUser = isBlocking
        ? await blockPublicUser(userToBlock.userId)
        : await unblockPublicUser(userToBlock.userId);

      const action = isBlocking ? 'suspended' : 'activated';
      toast.success(`Public user ${action} successfully!`);

      setUsers(prev => prev.map(u =>
        u.userId === userToBlock.userId
          ? updatedUser
          : u
      ));

      setIsBlockModalOpen(false);
      setUserToBlock(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `An error occurred while ${userToBlock.active ? 'suspending' : 'activating'} the public user`;
      toast.error(errorMessage);
      console.error('Block/Unblock error:', error);
    }
  };

  const openEditModal = (user: PublicUserProfile) => {
    setUserToEdit(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      status: user.active ? 'active' : 'suspended',
    });
    setEditErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!userToEdit) return;

    const errors: { name?: string; email?: string } = {};
    if (!editForm.name.trim()) errors.name = 'Name is required';
    if (!editForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email.trim())) {
      errors.email = 'Enter a valid email address';
    }
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setIsSavingEdit(true);
    try {
      const updatedUser = await updatePublicUser(userToEdit.userId, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        status: editForm.status as 'active' | 'suspended',
      });

      toast.success('Public user updated successfully!');
      setUsers(prev => prev.map(u => (u.userId === userToEdit.userId ? updatedUser : u)));
      setIsEditModalOpen(false);
      setUserToEdit(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred while updating the public user';
      toast.error(errorMessage);
      console.error('Update error:', error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRowClick = (user: PublicUserProfile) => {
    setSelectedUser(user);
    setIsProfileModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Public Users</h1>
        </div>
      </div>

      {/* Search and Filter Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${hasActiveFilters ? 'ring-2 ring-primary-500' : ''}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full font-medium">
                {Object.values(filters).filter((v) => v !== '').length}
              </span>
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <CustomSelect
                  value={filters.status}
                  onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Suspended' },
                  ]}
                  placeholder="All Statuses"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end space-x-3 mt-4">
                <button onClick={clearFilters} className="btn-secondary text-sm flex items-center">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoadingUsers && (
        <div className="card text-center py-12">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading public users...</h3>
          <p className="text-gray-600">Please wait while we fetch the public user records.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingUsers && users.length === 0 && (
        <div className="card text-center py-16">
          <UsersIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No public users yet</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto">
            There are currently no public users registered on the platform.
          </p>
        </div>
      )}

      {/* No Search Results */}
      {!isLoadingUsers && users.length > 0 && filteredUsers.length === 0 && (
        <div className="card text-center py-16">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No public users found</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
            {hasActiveFilters
              ? 'No public user records match your filters or search query. Try adjusting them.'
              : 'No public user records match your search query. Try adjusting your search term.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Public Users Table */}
      {!isLoadingUsers && filteredUsers.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Joined
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <UsersIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4 min-w-0">
                          <button
                            onClick={() => handleRowClick(user)}
                            className="text-sm font-medium text-gray-900 truncate hover:text-primary-700 transition-colors"
                            title="View public user details"
                          >
                            {user.name || 'Unnamed User'}
                          </button>
                          <div className="text-xs text-gray-500 sm:hidden truncate">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setUserToBlock(user);
                          setIsBlockModalOpen(true);
                        }}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          user.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                        title={`Click to ${user.active ? 'suspend' : 'activate'} public user`}
                      >
                        {user.active ? 'Active' : 'Suspended'}
                      </button>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-xs text-gray-500 hidden lg:table-cell">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-4">
                        <button
                          onClick={() => handleRowClick(user)}
                          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
                          title="View public user"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
                          title="Edit public user"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setUserToDelete(user);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Delete public user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of{' '}
                {filteredUsers.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-1">
                  {paginationPageWindow(currentPage, totalPages).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 rounded-lg text-sm font-medium border flex items-center justify-center transition-colors ${
                        currentPage === page
                          ? 'bg-primary-600 border-primary-600 text-white cursor-default'
                          : 'bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profile Detail Modal */}
      <Modal
        title="Public User Details"
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedUser(null);
        }}
        widthClassName="max-w-xl"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4 border-b border-gray-200 pb-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UsersIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedUser.name || 'Unnamed User'}</h2>
                <p className="text-sm text-primary-700 font-medium">Public User</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Email</span>
                <span className="text-gray-900">{selectedUser.email || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Status</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                    selectedUser.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {selectedUser.active ? 'Active' : 'Suspended'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Joined</span>
                <span className="text-gray-900">
                  {selectedUser.createdAt
                    ? new Date(selectedUser.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* Subscription Plan Details */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-primary-600" />
                <h3 className="text-sm font-semibold text-gray-900">Subscription Plan</h3>
              </div>

              {selectedUser.subscription ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Plan</span>
                      <span className="inline-flex items-center gap-2 text-gray-900">
                        {selectedUser.subscription.planName || 'N/A'}
                        {selectedUser.subscription.planTag && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-100 text-primary-700 uppercase tracking-wide">
                            {selectedUser.subscription.planTag}
                          </span>
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Status</span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${subscriptionStatusClasses(
                          selectedUser.subscription.status
                        )}`}
                      >
                        {selectedUser.subscription.status || 'N/A'}
                      </span>
                    </div>

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Billing Cycle</span>
                      <span className="text-gray-900">{selectedUser.subscription.billingCycle || 'N/A'}</span>
                    </div>

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Amount</span>
                      <span className="text-gray-900">
                        {formatCurrency(selectedUser.subscription.amount, selectedUser.subscription.currency || 'USD')}
                      </span>
                    </div>

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Start Date</span>
                      <span className="inline-flex items-center gap-1.5 text-gray-900">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        {formatDate(selectedUser.subscription.startDate)}
                      </span>
                    </div>

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">End Date</span>
                      <span className="inline-flex items-center gap-1.5 text-gray-900">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        {formatDate(selectedUser.subscription.endDate)}
                      </span>
                    </div>

                    {selectedUser.subscription.plan && (
                      <div>
                        <span className="font-semibold text-gray-600 block text-xs uppercase">Duration</span>
                        <span className="text-gray-900 capitalize">
                          {selectedUser.subscription.plan.durationValue}{' '}
                          {selectedUser.subscription.plan.durationType}
                        </span>
                      </div>
                    )}

                    <div>
                      <span className="font-semibold text-gray-600 block text-xs uppercase">Auto Renew</span>
                      <span className="text-gray-900">
                        {selectedUser.subscription.autoRenew ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No subscription plan is associated with this public user.
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Public User Modal */}
      <Modal
        title="Edit Public User"
        isOpen={isEditModalOpen && userToEdit !== null}
        onClose={() => {
          setIsEditModalOpen(false);
          setUserToEdit(null);
        }}
        widthClassName="max-w-lg"
      >
        {userToEdit && (
          <form onSubmit={handleEditSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, name: e.target.value }));
                  setEditErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="Enter full name"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  editErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              {editErrors.name && <p className="text-xs text-red-600 mt-1">{editErrors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, email: e.target.value }));
                  setEditErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="Enter email address"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  editErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              {editErrors.email && <p className="text-xs text-red-600 mt-1">{editErrors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <CustomSelect
                value={editForm.status}
                onChange={(val) => setEditForm((prev) => ({ ...prev, status: val }))}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'suspended', label: 'Suspended' },
                ]}
                placeholder="Select status"
              />
              {editForm.status === 'suspended' && (
                <p className="text-xs text-amber-600 mt-1">
                  A suspended user will not be able to log in to their account.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setUserToEdit(null);
                }}
                className="btn-secondary"
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSavingEdit}>
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Suspend/Activate Confirmation Modal */}
      <Modal
        title={userToBlock?.active ? 'Suspend Public User' : 'Activate Public User'}
        isOpen={isBlockModalOpen && userToBlock !== null}
        onClose={() => {
          setIsBlockModalOpen(false);
          setUserToBlock(null);
        }}
        widthClassName="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to {userToBlock?.active ? 'suspend' : 'activate'}{' '}
          <span className="font-semibold text-gray-900">{userToBlock?.name}</span>?
          {userToBlock?.active && (
            <span className="block mt-2 text-gray-500">
              A suspended user will not be able to log in to their account.
            </span>
          )}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsBlockModalOpen(false);
              setUserToBlock(null);
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleBlockUnblock}
            className={userToBlock?.active ? "btn-danger" : "btn-primary"}
          >
            {userToBlock?.active ? 'Suspend' : 'Activate'}
          </button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Public User"
        isOpen={isDeleteModalOpen && userToDelete !== null}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        widthClassName="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete{' '}
          <span className="font-semibold text-gray-900">{userToDelete?.name}</span>? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setUserToDelete(null);
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            Delete Public User
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PublicUsers;
